/**
 * VendorComparison - Side-by-side Vendor/Product Comparison View
 *
 * Displays comparison of multiple vendors or products:
 * - Side-by-side station score comparison
 * - Tier comparison
 * - Feature matrix
 * - Export comparison report
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-554 (Phase 5.1)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

type CertificationTier = 'platinum' | 'gold' | 'silver' | 'bronze' | null;
type StationType = 'data_quality' | 'ml_readiness' | 'ontology' | 'fair';

interface StationScores {
  data_quality: number;
  ml_readiness: number;
  ontology: number;
  fair: number;
}

interface ComparisonItem {
  id: string;
  type: 'vendor' | 'product';
  name: string;
  vendor_name?: string;
  tier: CertificationTier;
  overall_score: number;
  station_scores: StationScores;
  certified: boolean;
  expires_at?: string;
  category?: string;
}

interface ComparisonData {
  items: ComparisonItem[];
  comparison_date: string;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const STATION_LABELS: Record<StationType, string> = {
  data_quality: 'Data Quality',
  ml_readiness: 'ML Readiness',
  ontology: 'Ontology Alignment',
  fair: 'FAIR Compliance',
};

const TIER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  platinum: { bg: '#f0fdf4', text: '#166534', border: '#86efac' },
  gold: { bg: '#fefce8', text: '#a16207', border: '#fde047' },
  silver: { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
  bronze: { bg: '#fdf4e7', text: '#92400e', border: '#fcd34d' },
};

const STATIONS: StationType[] = ['data_quality', 'ml_readiness', 'ontology', 'fair'];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getScoreColor(score: number): string {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#ca8a04';
  return '#dc2626';
}

function formatTier(tier: CertificationTier): string {
  if (!tier) return 'Not Certified';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface ComparisonHeaderProps {
  items: ComparisonItem[];
  onRemove: (id: string) => void;
}

const ComparisonHeader: React.FC<ComparisonHeaderProps> = ({ items, onRemove }) => {
  return (
    <div className="comparison-header">
      <div className="comparison-header-label" />
      {items.map((item) => (
        <div key={item.id} className="comparison-header-item">
          <div className="comparison-item-name">{item.name}</div>
          {item.vendor_name && (
            <div className="comparison-item-vendor">{item.vendor_name}</div>
          )}
          <button
            className="comparison-remove-btn"
            onClick={() => onRemove(item.id)}
            title="Remove from comparison"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

interface TierRowProps {
  items: ComparisonItem[];
}

const TierRow: React.FC<TierRowProps> = ({ items }) => {
  return (
    <div className="comparison-row">
      <div className="comparison-row-label">Certification Tier</div>
      {items.map((item) => {
        const tierStyle = item.tier ? TIER_COLORS[item.tier] : { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' };
        return (
          <div key={item.id} className="comparison-row-value">
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '6px',
                backgroundColor: tierStyle.bg,
                color: tierStyle.text,
                border: `1px solid ${tierStyle.border}`,
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {formatTier(item.tier)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

interface OverallScoreRowProps {
  items: ComparisonItem[];
}

const OverallScoreRow: React.FC<OverallScoreRowProps> = ({ items }) => {
  const maxScore = Math.max(...items.map((i) => i.overall_score));

  return (
    <div className="comparison-row comparison-row-highlight">
      <div className="comparison-row-label">Overall Score</div>
      {items.map((item) => (
        <div key={item.id} className="comparison-row-value">
          <div
            style={{
              fontSize: '28px',
              fontWeight: 700,
              color: item.overall_score === maxScore ? '#16a34a' : '#374151',
            }}
          >
            {item.overall_score}%
          </div>
          {item.overall_score === maxScore && items.length > 1 && (
            <span
              style={{
                fontSize: '11px',
                color: '#16a34a',
                display: 'block',
                marginTop: '2px',
              }}
            >
              Highest
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

interface StationScoreRowProps {
  station: StationType;
  items: ComparisonItem[];
}

const StationScoreRow: React.FC<StationScoreRowProps> = ({ station, items }) => {
  const scores = items.map((i) => i.station_scores[station]);
  const maxScore = Math.max(...scores);

  return (
    <div className="comparison-row">
      <div className="comparison-row-label">{STATION_LABELS[station]}</div>
      {items.map((item, _idx) => {
        const score = item.station_scores[station];
        const isHighest = score === maxScore && items.length > 1;
        const color = getScoreColor(score);

        return (
          <div key={item.id} className="comparison-row-value">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  flex: 1,
                  height: '8px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${score}%`,
                    backgroundColor: color,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: isHighest ? 600 : 400,
                  color: isHighest ? '#16a34a' : '#374151',
                  minWidth: '40px',
                }}
              >
                {score}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface ExpiryRowProps {
  items: ComparisonItem[];
}

const ExpiryRow: React.FC<ExpiryRowProps> = ({ items }) => {
  return (
    <div className="comparison-row">
      <div className="comparison-row-label">Expires</div>
      {items.map((item) => (
        <div key={item.id} className="comparison-row-value">
          {item.expires_at ? formatDate(item.expires_at) : '-'}
        </div>
      ))}
    </div>
  );
};

interface CategoryRowProps {
  items: ComparisonItem[];
}

const CategoryRow: React.FC<CategoryRowProps> = ({ items }) => {
  return (
    <div className="comparison-row">
      <div className="comparison-row-label">Category</div>
      {items.map((item) => (
        <div key={item.id} className="comparison-row-value">
          {item.category || '-'}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const VendorComparison: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse IDs from URL - memoize to prevent dependency changes on every render
  const itemIds = useMemo(() => {
    return searchParams.get('ids')?.split(',').filter(Boolean) || [];
  }, [searchParams]);

  // Fetch comparison data
  const fetchComparison = useCallback(async () => {
    if (itemIds.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/vendor-reports/comparison?ids=${itemIds.join(',')}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch comparison: ${response.statusText}`);
      }

      const data = await response.json();
      setComparisonData(data);
    } catch (err) {
      console.error('Error fetching comparison:', err);
      setError(err instanceof Error ? err.message : 'Failed to load comparison');
    } finally {
      setLoading(false);
    }
  }, [itemIds]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  // Remove item from comparison
  const handleRemoveItem = (id: string) => {
    const newIds = itemIds.filter((i) => i !== id);
    if (newIds.length > 0) {
      setSearchParams({ ids: newIds.join(',') });
    } else {
      navigate('/vendors');
    }
  };

  // Export comparison
  const handleExport = async () => {
    try {
      const response = await fetch(
        `/api/vendor-reports/comparison/export?ids=${itemIds.join(',')}&format=pdf`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendor-comparison-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export comparison');
    }
  };

  // Render empty state
  if (!loading && itemIds.length === 0) {
    return (
      <div className="vendor-comparison-page">
        <div className="comparison-empty">
          <h2>No Items to Compare</h2>
          <p>Select vendors or products from the dashboard to compare them.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/vendors')}
          >
            Go to Vendor Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Render loading
  if (loading) {
    return (
      <div className="vendor-comparison-page">
        <div className="comparison-loading">
          <div className="spinner" />
          <p>Loading comparison...</p>
        </div>
      </div>
    );
  }

  // Render error
  if (error) {
    return (
      <div className="vendor-comparison-page">
        <div className="comparison-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchComparison}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const items = comparisonData?.items || [];

  return (
    <div className="vendor-comparison-page">
      {/* Header */}
      <div className="comparison-page-header">
        <div className="comparison-page-title">
          <button
            className="btn btn-back"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
          <h1>Vendor Comparison</h1>
          <span className="comparison-count">{items.length} items</span>
        </div>
        <div className="comparison-page-actions">
          <button className="btn btn-secondary" onClick={handleExport}>
            Export PDF
          </button>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="comparison-table-container">
        <div className="comparison-table">
          {/* Header with item names */}
          <ComparisonHeader items={items} onRemove={handleRemoveItem} />

          {/* Tier Row */}
          <TierRow items={items} />

          {/* Overall Score */}
          <OverallScoreRow items={items} />

          {/* Station Scores */}
          <div className="comparison-section">
            <div className="comparison-section-title">Station Scores</div>
            {STATIONS.map((station) => (
              <StationScoreRow key={station} station={station} items={items} />
            ))}
          </div>

          {/* Additional Info */}
          <div className="comparison-section">
            <div className="comparison-section-title">Details</div>
            <CategoryRow items={items} />
            <ExpiryRow items={items} />
          </div>
        </div>
      </div>

      {/* Add More Button */}
      {items.length < 4 && (
        <div className="comparison-add-more">
          <button
            className="btn btn-outline"
            onClick={() => navigate('/vendors?mode=compare')}
          >
            + Add Item to Comparison
          </button>
        </div>
      )}
    </div>
  );
};

export default VendorComparison;
